import React, { FC, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import { Order } from 'models/order';
import { TransportServices } from 'models/transport';
import { createTransportForOrderTemplate } from 'apollo/templates/transport/mutation.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import TransportDatePicker from 'components/shared/transport-date-picker/transport-date-picker.component';
import TransportDeadline from 'components/shared/transport-deadline/transport-deadline.component';
import { TRANSPORT_DATE_KEYS, transportDateHelper } from 'utils/transport-date-helper';
import { CONSTANTS } from 'config/constants';

const { TRANSPORT_DEADLINES } = CONSTANTS;

type ScheduleDeliveryDialogProps = {
    open: boolean;
    onClose: () => void;
    order: Order;
    onDeliveryCreated?: () => void;
};

const PACKAGE_COUNT_OPTIONS = Array.from(Array(20).keys()).map((value) => value + 1);

const ScheduleDeliveryDialog: FC<ScheduleDeliveryDialogProps> = ({
    open,
    onClose,
    order,
    onDeliveryCreated,
}: ScheduleDeliveryDialogProps) => {
    const { t: translate } = useTranslation(['common', 'transport']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [activeStep, setActiveStep] = useState(0);
    const [transportDay, setTransportDay] = useState<Date | null>(null);
    const [manualPackageCount, setManualPackageCount] = useState<number>(1);
    const [comment, setComment] = useState<string>('');
    const [internalMessage, setInternalMessage] = useState<string>('');
    const [deadline, setDeadline] = useState<string>(TRANSPORT_DEADLINES[0]);

    const [createTransportForOrder, { loading }] = useMutation(createTransportForOrderTemplate, {
        onCompleted: () => {
            onDeliveryCreated?.();
            resetAndClose();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const resetAndClose = () => {
        setActiveStep(0);
        setTransportDay(null);
        setManualPackageCount(1);
        setComment('');
        setInternalMessage('');
        setDeadline(TRANSPORT_DEADLINES[0]);
        onClose();
    };

    const navigateNext = () => setActiveStep((previousStep) => previousStep + 1);
    const navigateBack = () => setActiveStep((previousStep) => previousStep - 1);

    const placeOrder = () => {
        if (!transportDay) {
            return;
        }
        const dentistName = order.dentist ? order.dentist.fullName : order.created.by.fullName;
        const dateZeroTime = new Date(transportDateHelper.nextWorkingDay(transportDay, false).setHours(0, 0, 0, 0));
        createTransportForOrder({
            variables: {
                clinic: order.deliveryDetails?.destination?._id,
                serviceCode: TransportServices.DELIVERY,
                transportDay: moment(dateZeroTime).format('YYYY-MM-DD'),
                userSelectedDate: moment(moment(transportDay).toDate()).format('YYYY-MM-DD'),
                manualPackageCount: 0,
                comment,
                internalMessage,
                dentistName,
                orderId: order._id,
                deadline,
            },
        });
    };

    const getSteps = () => [translate('transport:WHEN'), translate('transport:DETAILS')];

    const formatPickupDate = () => {
        const formattedDate = transportDateHelper.transportDateFormatter(
            transportDateHelper.nextWorkingDayAsString(transportDay, false),
            'date',
        );
        if (TRANSPORT_DATE_KEYS.includes(formattedDate)) {
            return translate(`transport:${formattedDate}`);
        }
        return formattedDate;
    };

    return (
        <Dialog maxWidth="md" open={open} onClose={resetAndClose} fullWidth>
            <DialogTitle>{translate('transport:ADD_DELIVERY_TRANSPORT')}</DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep}>
                    {getSteps().map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box mt={4}>
                    {activeStep === 0 ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TransportDatePicker
                                    date={transportDay}
                                    onDateChange={setTransportDay}
                                    type={TransportServices.DELIVERY}
                                />
                                {transportDateHelper.isSameDayDelivery(transportDay) ? (
                                    <Box mt={1}>
                                        <Alert severity="warning">{translate('transport:SAME_DAY_DELIVERY')}</Alert>
                                    </Box>
                                ) : undefined}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TransportDeadline
                                    readOnly={false}
                                    deadline={deadline}
                                    onDeadlineChange={setDeadline}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Alert icon={false} severity="info">
                                    <AlertTitle>
                                        <Typography variant="subtitle2">
                                            {translate('transport:PICKUP_FROM_YOU_DATE')}
                                        </Typography>
                                    </AlertTitle>
                                    <Box>{formatPickupDate()}</Box>
                                </Alert>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="add-non-waybill-delivery-package-count-label">
                                        {translate('transport:NUMBER_OF_PACKAGES')}
                                    </InputLabel>
                                    <Select
                                        labelId="add-non-waybill-delivery-package-count-label"
                                        value={manualPackageCount}
                                        label={translate('transport:NUMBER_OF_PACKAGES')}
                                        onChange={(event) => setManualPackageCount(event.target.value as number)}
                                        readOnly={true}
                                        disabled={true}
                                    >
                                        {PACKAGE_COUNT_OPTIONS.map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    value={comment}
                                    label={translate('transport:MESSAGE_TO_CARRIER')}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => setComment(event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={internalMessage}
                                    label={translate('transport:INTERNAL_MESSAGE')}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                        setInternalMessage(event.target.value)
                                    }
                                />
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Box flexGrow={1} display="flex" gap={1}>
                    {activeStep === 1 ? <Button onClick={navigateBack}>{translate('common:BACK')}</Button> : undefined}
                </Box>
                <Button onClick={resetAndClose}>{translate('transport:CANCEL')}</Button>
                {activeStep === 0 ? (
                    <Button variant="contained" disabled={!transportDay} onClick={navigateNext}>
                        {translate('common:NEXT_STEP')}
                    </Button>
                ) : (
                    <LoadingButton loading={loading} variant="contained" onClick={placeOrder}>
                        {translate('transport:ORDER_TRANSPORT')}
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleDeliveryDialog;
